import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useShellHeader, useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import DocketBrowser from '@/components/dockets/DocketBrowser';
import DocketEntries from '@/components/dockets/DocketEntries';
import DocketHeader from '@/components/dockets/DocketHeader';
import type { DocketCandidate } from '@/components/dockets/schema';
import { useLinkedDocket } from '@/components/dockets/useLinkedDocket';
import { FRESH_DOCKET_WINDOW_MS } from '@/components/dockets/format';
import PageError from '@/components/shared/Error';
import Loading from '@/components/shared/Loading';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import { Button } from '@/components/ui/NewButton';
import useMatter from '@/hooks/useMatter';
import { apiErrorMessage } from '@/lib/api-client';

// Wait this long after dockets have loaded (and in-flight syncs have settled)
// before treating a missing citation guid as not found.
const CITATION_DEEP_LINK_SETTLE_MS = 4000;

// Sync statuses where entries may still be arriving. Defer the missing-citation
// toast while any linked docket is in one of these, up to FRESH_DOCKET_WINDOW_MS.
const ENTRY_ARRIVAL_SYNC_STATUSES = new Set(['not_started', 'metadata_synced', 'syncing']);

const Dockets = () => {
  const { isLoading, isError, matter } = useMatter();
  const { linkedDockets, linkedIds, linkDocket, unlinkDocket, isLoading: docketsLoading } =
    useLinkedDocket(matter?.guid);
  const navigate = useNavigate({ from: '/matter/$slug/dockets' });
  const { entry: entryParam } = useSearch({ from: '/matter/$slug/dockets' });

  // Source keys (`provider:sourceDocketId`) of linked dockets, used to hide
  // already-linked results in the browser.
  const linkedKeys = useMemo(
    () => linkedDockets.map(({ docket }) => `${docket.sourceProvider}:${docket.sourceDocketId}`),
    [linkedDockets],
  );

  const [activeTab, setActiveTab] = useState<string>('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [highlightEntryGuid, setHighlightEntryGuid] = useState<string | null>(null);
  const [highlightToken, setHighlightToken] = useState(0);

  // Keep the active tab valid as dockets are linked/unlinked: always point at a
  // linked docket (or nothing when none are linked).
  useEffect(() => {
    if (!linkedIds.includes(activeTab)) {
      setActiveTab(linkedIds[0] ?? '');
    }
  }, [activeTab, linkedIds]);

  // Deep link from a Case Partner docket citation. The cited guid may reference a
  // whole docket (list_dockets) or a specific entry (search/get). Select the
  // owning docket so DocketEntries can scroll to and highlight the entry when one
  // matches. Clear `entry` after applying so the user can switch tabs, and so a
  // later click of the same citation is a real search change rather than a no-op.
  const missingSinceRef = useRef<{ guid: string; startedAt: number } | null>(null);
  useEffect(() => {
    // useDockets(undefined) reports not-loading while matter is unresolved, so
    // also wait for matter before starting the missing-citation timer.
    if (!entryParam || docketsLoading || isLoading || !matter) return;

    const clearEntrySearch = () => {
      void navigate({ search: {}, replace: true });
    };

    const docketMatch = linkedDockets.find(({ docket }) => docket.id === entryParam);
    if (docketMatch) {
      missingSinceRef.current = null;
      setActiveTab(docketMatch.docket.id);
      setHighlightEntryGuid(null);
      clearEntrySearch();
      return;
    }

    const entryMatch = linkedDockets.find(({ docket }) =>
      docket.entries.some((docketEntry) => docketEntry.guid === entryParam),
    );
    if (entryMatch) {
      missingSinceRef.current = null;
      setActiveTab(entryMatch.docket.id);
      setHighlightEntryGuid(entryParam);
      setHighlightToken((token) => token + 1);
      clearEntrySearch();
      return;
    }

    if (missingSinceRef.current?.guid !== entryParam) {
      missingSinceRef.current = { guid: entryParam, startedAt: Date.now() };
    }
    const elapsed = Date.now() - missingSinceRef.current.startedAt;
    const awaitingEntries = linkedDockets.some(({ docket }) =>
      ENTRY_ARRIVAL_SYNC_STATUSES.has(docket.syncStatus),
    );
    // While entries may still arrive, keep waiting — but never pin ?entry=
    // longer than the fresh-docket window if sync is stuck.
    if (awaitingEntries && elapsed < FRESH_DOCKET_WINDOW_MS) {
      return;
    }
    const remaining = Math.max(0, CITATION_DEEP_LINK_SETTLE_MS - elapsed);
    const timer = window.setTimeout(() => {
      toast.error('Could not find that docket.');
      missingSinceRef.current = null;
      clearEntrySearch();
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [docketsLoading, entryParam, isLoading, linkedDockets, matter, navigate]);

  useShellHeader({
    header: 'Dockets',
    subheader: 'Link and review US federal court dockets for this matter',
  });

  const isUsMatter = matter?.region?.toUpperCase() === 'US';

  // Render the docket switcher as tabs in the shell header (below the title),
  // matching other matter pages. Hidden until at least one docket is linked.
  // Adding further dockets happens in a modal via the button after the last tab.
  const headerTabs = useMemo(() => {
    if (!isUsMatter || linkedDockets.length === 0) return null;
    const items = linkedDockets.map(({ docket }) => ({
      value: docket.id,
      label: docket.caseNumber,
    }));
    return (
      <HeaderTabs
        items={items}
        value={activeTab}
        onChange={setActiveTab}
        ariaLabel="Linked dockets"
        idBase="dockets"
        trailing={
          <Button
            variant="ghost"
            size="sm"
            text="Add Docket"
            icon={Plus}
            onClick={() => setIsAddOpen(true)}
            className="-mx-1.5 -my-1 h-auto gap-1.5 px-2.5 py-1 text-[13px] font-medium text-slate-500 hover:bg-accent hover:text-accent-foreground"
          />
        }
      />
    );
  }, [isUsMatter, linkedDockets, activeTab]);

  useShellHeaderBelow(headerTabs);

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return <PageError message={isError.message} />;
  }

  if (!matter) {
    return <NoCaseSelected />;
  }

  // Dockets are a US-only feature. Non-US matters should not reach this page via
  // the sidebar, but guard against direct navigation.
  if (!isUsMatter) {
    return (
      <div className="p-6">
        <PageError message="Dockets are only available for US jurisdiction matters." />
      </div>
    );
  }

  const handleLink = async (candidate: DocketCandidate) => {
    try {
      const docket = await linkDocket(candidate);
      toast.success(`Linked ${docket.caseName || candidate.caseName || 'docket'} to this matter`);
      setActiveTab(docket.id);
      setIsAddOpen(false);
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Could not link this docket. Please try again.');
    }
  };

  const handleUnlink = async (docketId: string) => {
    const docket = linkedDockets.find(({ docket }) => docket.id === docketId)?.docket;
    try {
      await unlinkDocket(docketId);
      toast.success(`Unlinked ${docket?.caseName ?? 'docket'} from this matter`);
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Could not unlink this docket. Please try again.');
    }
  };

  // No linked dockets yet: show only the add-docket flow.
  if (linkedDockets.length === 0) {
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-6">
        <div className="mt-12 md:mt-24">
          <DocketBrowser matterGuid={matter.guid} onLink={handleLink} excludeKeys={linkedKeys} />
        </div>
      </div>
    );
  }

  const activeDocket = linkedDockets.find(({ docket }) => docket.id === activeTab)?.docket;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-6">
      {activeDocket && (
        <>
          <DocketHeader docket={activeDocket} onUnlink={() => handleUnlink(activeDocket.id)} />
          <DocketEntries
            docket={activeDocket}
            matterGuid={matter.guid}
            highlightEntryGuid={highlightEntryGuid}
            highlightToken={highlightToken}
          />
        </>
      )}

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Link a docket to this matter</DialogTitle>
            <DialogDescription>
              Search US federal court dockets and link one to this matter.
            </DialogDescription>
          </DialogHeader>
          <DocketBrowser
            matterGuid={matter.guid}
            onLink={handleLink}
            excludeKeys={linkedKeys}
            hideHeader
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dockets;
